import { useState } from 'react'
import { Mail, Phone, Star, UserRound } from 'lucide-react'
import { formatCurrency, formatDateLong } from '@hotel/shared/utils'
import { useCustomerLogin, useCustomerRegister, useCustomerReservations } from '../api/hooks'
import type { Customer } from '../api/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { PhoneInput } from '@/components/PhoneInput'

const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  checked_in: 'Check-in',
  checked_out: 'Check-out',
  cancelled: 'Cancelada',
  no_show: 'No Show',
}

const fieldLabel = 'mb-2 block text-sm font-medium text-wood-dark'

export function AccountPage({
  customer,
  setCustomerAuth,
}: {
  customer: Customer | null
  setCustomerAuth: (next: { token: string; customer: Customer } | null) => void
}) {
  const [form, setForm] = useState({
    email: customer?.email || '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
  })

  const reservationsQuery = useCustomerReservations(customer?.id)
  const loginMutation = useCustomerLogin()
  const registerMutation = useCustomerRegister()

  const handleLogin = () => {
    loginMutation.mutate(
      { email: form.email, password: form.password },
      { onSuccess: (data) => setCustomerAuth(data) },
    )
  }

  const handleRegister = () => {
    registerMutation.mutate(form, { onSuccess: (data) => setCustomerAuth(data) })
  }

  return (
    <section className="bg-cream-light pt-32 pb-20">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-[2fr_1fr]">
        <div>
          <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-terracotta">
            Área de cliente
          </p>
          <h1 className="mb-2 font-heading text-3xl font-bold text-wood-dark md:text-4xl">
            {customer ? `Hola, ${customer.first_name}` : 'Ingresa para ver tus reservas'}
          </h1>
          <p className="mb-8 text-stone">
            Si no tienes cuenta, puedes crearla con correo y teléfono obligatorios.
          </p>

          {customer ? (
            <div className="space-y-4">
              {(reservationsQuery.data || []).map((reservation) => (
                <Card key={reservation.id}>
                  <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-terracotta">
                        Confirmación
                      </p>
                      <h3 className="font-heading text-xl font-bold text-wood-dark">
                        {reservation.confirmation_code}
                      </h3>
                      <p className="text-sm text-stone">{reservation.room_type_name}</p>
                      <Badge variant="gold" className="mt-2">
                        {statusLabels[reservation.status] || reservation.status}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-stone">
                        {formatDateLong(reservation.check_in_date)} —{' '}
                        {formatDateLong(reservation.check_out_date)}
                      </p>
                      <strong className="block text-xl font-bold text-terracotta">
                        {formatCurrency(reservation.grand_total_with_tax ?? reservation.total_amount)}
                      </strong>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {reservationsQuery.data?.length === 0 && (
                <Card>
                  <CardContent className="p-6 text-center text-stone">
                    Tu cuenta todavía no tiene reservaciones asociadas.
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 font-heading text-xl font-bold text-wood-dark">
                    Ingresar
                  </h3>
                  <div className="space-y-3">
                    <label className="block">
                      <span className={fieldLabel}>Correo</span>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, email: e.target.value }))
                        }
                      />
                    </label>
                    <label className="block">
                      <span className={fieldLabel}>Contraseña</span>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, password: e.target.value }))
                        }
                      />
                    </label>
                    <Button className="w-full" onClick={handleLogin}>
                      Ingresar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 font-heading text-xl font-bold text-wood-dark">
                    Crear cuenta
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className={fieldLabel}>Nombre</span>
                      <Input
                        value={form.first_name}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, first_name: e.target.value }))
                        }
                      />
                    </label>
                    <label className="block">
                      <span className={fieldLabel}>Apellido</span>
                      <Input
                        value={form.last_name}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, last_name: e.target.value }))
                        }
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className={fieldLabel}>Correo</span>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, email: e.target.value }))
                        }
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className={fieldLabel}>Teléfono</span>
                      <PhoneInput
                        value={form.phone}
                        onChange={(next) =>
                          setForm((prev) => ({ ...prev, phone: next }))
                        }
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className={fieldLabel}>Contraseña</span>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, password: e.target.value }))
                        }
                      />
                    </label>
                    <Button
                      variant="secondary"
                      className="sm:col-span-2"
                      onClick={handleRegister}
                    >
                      Crear cuenta
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <aside className="space-y-3">
          <Card>
            <CardContent className="space-y-3 p-6 text-sm text-stone">
              <div className="flex items-center gap-2">
                <UserRound size={18} className="text-terracotta" /> Cuenta opcional para
                seguimiento de reservas
              </div>
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-terracotta" /> Teléfono obligatorio
              </div>
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-terracotta" /> Correo obligatorio
              </div>
              <div className="flex items-center gap-2">
                <Star size={18} className="text-terracotta" /> Reserva pública sin exponer
                ocupación interna
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </section>
  )
}
